import { getUploadUrl, uploadToS3, getDownloadUrl } from "../api";
import { toBase64, fromBase64 } from "./chatUtils";

/**
 * Encrypt a file using AES-GCM with the shared key
 * @param {File} file - The file to encrypt
 * @param {CryptoKey} sharedKey - The shared encryption key
 * @returns {Promise<{encryptedBlob: Blob, iv: string, key: string}>}
 */
export const encryptFile = async (file, sharedKey) => {
  // Generate a random IV for this file
  const iv = crypto.getRandomValues(new Uint8Array(12));
  
  // Read file as ArrayBuffer
  const fileBuffer = await file.arrayBuffer();
  
  // Encrypt the file
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    sharedKey,
    fileBuffer
  );
  
  // Convert to Blob for upload
  const encryptedBlob = new Blob([encrypted], { type: "application/octet-stream" });
  
  return {
    encryptedBlob,
    iv: toBase64(iv),
    key: toBase64(iv), // Using IV as key for now, you can generate separate key if needed
  };
};

/**
 * Decrypt a file using AES-GCM with the shared key
 * @param {ArrayBuffer} encryptedData - The encrypted file data
 * @param {string} ivBase64 - The IV used for encryption (base64)
 * @param {CryptoKey} sharedKey - The shared encryption key
 * @param {string} mimeType - The original file MIME type
 * @returns {Promise<Blob>}
 */
export const decryptFile = async (encryptedData, ivBase64, sharedKey, mimeType) => {
  try {
    const iv = fromBase64(ivBase64);
    
    if (iv.length !== 12) {
      throw new Error("Invalid IV length");
    }
    
    // Decrypt the file
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      sharedKey,
      encryptedData
    );
    
    // Return as Blob with original MIME type
    return new Blob([decrypted], { type: mimeType });
  } catch (error) {
    console.error("File decryption failed:", error);
    throw new Error("Failed to decrypt file");
  }
};

/**
 * Upload an encrypted file to S3
 * @param {File} file - The original file
 * @param {CryptoKey} sharedKey - The shared encryption key
 * @returns {Promise<{s3Key: string, encryptedKey: string, thumbnail: string|null, duration: number|null}>}
 */
export const uploadEncryptedFile = async (file, sharedKey) => {
  // Encrypt the file
  const { encryptedBlob, iv } = await encryptFile(file, sharedKey);
  
  // Get presigned upload URL
  const { data } = await getUploadUrl(
    file.name,
    "application/octet-stream", // Always upload as binary
    encryptedBlob.size
  );
  
  // Upload to S3
  await uploadToS3(data.uploadUrl, encryptedBlob);
  
  // Generate thumbnail for images/videos
  const thumbnail = await generateThumbnail(file);
  
  // Get duration for audio/video (if possible)
  const duration = await getMediaDuration(file);
  
  return {
    s3Key: data.key, // The S3 key returned from backend
    encryptedKey: iv, // Store the IV as the encryption key
    thumbnail, // Base64 thumbnail or null
    duration, // Duration in seconds or null
  };
};

/**
 * Download and decrypt a file from S3
 * @param {string} s3Key - The S3 key (not full URL)
 * @param {string} mediaKey - The encryption key (IV)
 * @param {CryptoKey} sharedKey - The shared encryption key
 * @param {string} mediaType - The original MIME type
 * @returns {Promise<Blob>}
 */
export const downloadAndDecryptFile = async (s3Key, mediaKey, sharedKey, mediaType) => {
  // Get presigned download URL from backend
  const { data } = await getDownloadUrl(s3Key);
  
  // Download the encrypted file
  const response = await fetch(data.downloadUrl);
  if (!response.ok) {
    throw new Error("Failed to download file");
  }
  
  const encryptedData = await response.arrayBuffer();
  
  // Decrypt and return
  return await decryptFile(encryptedData, mediaKey, sharedKey, mediaType);
};

/**
 * Generate a thumbnail for images/videos
 * @param {File} file - The file to generate thumbnail for
 * @param {number} maxWidth - Maximum thumbnail width
 * @param {number} maxHeight - Maximum thumbnail height
 * @returns {Promise<string|null>} - Base64 encoded thumbnail or null
 */
export const generateThumbnail = async (file, maxWidth = 200, maxHeight = 200) => {
  return new Promise((resolve) => {
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/jpeg", 0.7));
        };
        img.onerror = () => resolve(null);
        img.src = e.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.onloadedmetadata = () => {
        video.currentTime = 1; // Seek to 1 second for thumbnail
      };
      video.onseeked = () => {
        const canvas = document.createElement("canvas");
        let width = video.videoWidth;
        let height = video.videoHeight;
        
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      video.onerror = () => resolve(null);
      video.src = URL.createObjectURL(file);
    } else {
      resolve(null); // No thumbnail for other file types
    }
  });
};

/**
 * Get file type category
 * @param {string} mimeType - The MIME type
 * @returns {string} - 'image', 'video', 'audio', or 'document'
 */
export const getFileCategory = (mimeType) => {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  return "document";
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted string (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  return (bytes / (1024 * 1024 * 1024)).toFixed(1) + " GB";
};

/**
 * Get duration of audio/video file
 * @param {File} file - The media file
 * @returns {Promise<number|null>} - Duration in seconds or null
 */
export const getMediaDuration = async (file) => {
  return new Promise((resolve) => {
    if (file.type.startsWith("audio/") || file.type.startsWith("video/")) {
      const media = file.type.startsWith("video/") 
        ? document.createElement("video")
        : document.createElement("audio");
      
      media.onloadedmetadata = () => {
        URL.revokeObjectURL(media.src);
        resolve(Math.round(media.duration));
      };
      
      media.onerror = () => {
        URL.revokeObjectURL(media.src);
        resolve(null);
      };
      
      media.src = URL.createObjectURL(file);
    } else {
      resolve(null);
    }
  });
};
